import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { promisify } from '@/lib/helpers';
import {
  textractTableFieldsMappingSelector,
  textractLineItemsBoundTableHeadersSelector,
  textractFirstTablePageSelector,
  learnDocumentExtractedTableFieldsAction,
  textractLineItemsTableCellsSelector,
} from '@/domain/textract';
import { useTextractFieldsMapping } from '@/domain/textract/queries';
import type { BoundField } from '@/domain/textract/schemas';
import { jeMatchedLinesSelector } from '@/domain/journal';
import { TwoWayMatchTableCells } from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageTwoWayMatchLayer/TwoWayMatchTableCells';
import { TwoWayMatchColumnBindings } from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageTwoWayMatchLayer/TwoWayMatchColumnBindings';
import { TwoWayMatchRowMarkers } from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageTwoWayMatchLayer/TwoWayMatchRowMarkers';
import { Map } from 'immutable';

import { displayedTwoWayMatchCell } from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageTwoWayMatchLayer/twoWayMatchGeometry';

import TwoWayMatchTableDragSource from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageTwoWayMatchLayer/TwoWayMatchTableDragSource';

type PageTwoWayMatchLayerProps = {
  width: number;
  height: number;
  scale: number;
  page: number;
  userRotation: number;
  turnFromUpright: number;
};

export const PageTwoWayMatchLayer = ({
  width,
  height,
  scale,
  page,
  userRotation,
  turnFromUpright,
}: PageTwoWayMatchLayerProps) => {
  const dispatch = useDispatch();
  const { companyId, documentId } = useParams();
  const [activeBinding, setActiveBinding] = useState<string | null>(null);
  const [learnedFieldsLoading, setLearnedFieldsLoading] = useState(false);
  const [boundFields, setBoundFields] = useState<Record<string, BoundField>>({});
  const [isDirty, setDirty] = useState(false);
  // Fetched as soon as the layer mounts, not on the first dropdown open: whether a column
  // counts as bound is decided against this catalogue.
  const {
    data: mappingFields,
    isFetching: isFetchingFields,
    isLoading: isFieldsPending,
  } = useTextractFieldsMapping(companyId ?? null, documentId ?? '', !!documentId);
  const pageTableCellsSelector = useMemo(() => textractLineItemsTableCellsSelector(page), [page]);
  const tableCells = useSelector(pageTableCellsSelector);
  const columns = useSelector(textractTableFieldsMappingSelector);
  const mappingHeaders = useSelector(textractLineItemsBoundTableHeadersSelector);
  const matchedLines = useSelector(jeMatchedLinesSelector);
  const tablePage = useSelector(textractFirstTablePageSelector);

  const rowMatchStatuses = useMemo(
    () =>
      matchedLines
        .reduce((acc, v) => {
          const flatValue = v.flatten();
          // 1 - line, 0 - status
          return acc.set(flatValue.get(1), flatValue.get(0));
        }, Map())
        .filter((v) => v),
    [matchedLines],
  );

  const learnedFields = useMemo(
    () =>
      mappingHeaders.reduce((acc: Record<string, BoundField>, { key, header }) => {
        // a header missing from the catalogue must not become a binding: it would put an empty
        // object into the learn request payload
        const field = mappingFields?.find(({ name }) => name === header);
        const mapping = columns.find(({ name }) => name === key);
        return field ? { ...acc, [key]: { ...field, mandatory: mapping?.metadata.mandatory } } : acc;
      }, {}),
    [mappingHeaders, mappingFields, columns],
  );

  const displayedColumns = useMemo(
    () =>
      columns.map((column) => ({
        ...column,
        bindingCell: displayedTwoWayMatchCell(column.bindingCell, userRotation),
      })),
    [columns, userRotation],
  );

  const displayedTableCells = useMemo(
    () =>
      tableCells.map((tableCell) => ({
        ...tableCell,
        Geometry: {
          ...tableCell.Geometry,
          Polygon: displayedTwoWayMatchCell(tableCell.Geometry.Polygon, userRotation),
        },
      })),
    [tableCells, userRotation],
  );

  useEffect(() => {
    setBoundFields(learnedFields);
  }, [learnedFields]);

  useEffect(() => {
    // change binding should trigger learn api invocation
    if (isDirty) {
      setLearnedFieldsLoading(true);
      const promisifyDispatch = (args) => dispatch(learnDocumentExtractedTableFieldsAction(args));
      promisify(promisifyDispatch, boundFields).finally(() => {
        setLearnedFieldsLoading(false);
      });
    }
  }, [boundFields, isDirty, dispatch]);

  const bindField = (index: number, mandatory: boolean | undefined) => {
    const field = mappingFields?.[index];
    if (activeBinding === null || !field) return;

    setBoundFields((prevState) => {
      const current = prevState[activeBinding];
      // clicking the bound field again with an unchanged mandatory flag unbinds the column
      if (current?.name === field.name && current.mandatory === mandatory) {
        const { [activeBinding]: _unbound, ...rest } = prevState;
        return rest;
      }
      return { ...prevState, [activeBinding]: { ...field, mandatory } };
    });
    setDirty(true);
  };

  const boundColumnIds = columns
    .filter((field) => Object.keys(boundFields).includes(field.name))
    .map((cell) => cell.id);

  // form state for active dropdown
  const selectedIndex = useMemo(() => {
    const boundName = activeBinding === null ? undefined : boundFields[activeBinding]?.name;
    const index = mappingFields?.findIndex(({ name }) => name === boundName) ?? -1;
    return index >= 0 ? index : null;
  }, [activeBinding, boundFields, mappingFields]);

  const boundFieldsIndices = useMemo(
    () =>
      mappingFields
        ? Object.values(boundFields).map(({ name }) => mappingFields.findIndex((field) => field.name === name))
        : [],
    [boundFields, mappingFields],
  );

  return (
    <div>
      <TwoWayMatchTableCells
        rowMatchStatuses={rowMatchStatuses}
        width={width * scale}
        height={height}
        tableCells={displayedTableCells}
        page={page}
      />
      <TwoWayMatchRowMarkers
        width={width * scale}
        scale={scale}
        height={height}
        page={page}
        userRotation={userRotation}
        turnFromUpright={turnFromUpright}
      />
      {page === tablePage ? (
        <TwoWayMatchColumnBindings
          onClick={setActiveBinding}
          width={width * scale}
          height={height}
          turnFromUpright={turnFromUpright}
          fields={mappingFields ?? []}
          columns={displayedColumns}
          onSelect={bindField}
          selectedFieldIndex={selectedIndex}
          disabledFields={boundFieldsIndices}
          boundColumnIds={boundColumnIds}
          isLoading={learnedFieldsLoading || isFetchingFields}
          isPending={isFieldsPending}
        />
      ) : null}
      {page === tablePage && displayedTableCells.length > 0 && (
        <TwoWayMatchTableDragSource
          width={width * scale}
          height={height}
          page={page}
          tableCells={displayedTableCells}
          columns={displayedColumns}
        />
      )}
    </div>
  );
};
